import { useCallback, useEffect, useRef, useState } from "react";
import measurementService from "../../services/measurement-service";

const SAVE_STATE = {
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
  ERROR: "error",
};

export function useAutoSave({ debounceMs = 600, persist } = {}) {
  const [saveState, setSaveState] = useState(SAVE_STATE.IDLE);
  const timerRef = useRef(null);
  const pendingPayloadRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const payload = pendingPayloadRef.current;
    if (!payload) {
      return true;
    }
    pendingPayloadRef.current = null;

    if (isMountedRef.current) {
      setSaveState(SAVE_STATE.SAVING);
    }

    try {
      if (persist) {
        await persist(payload);
      } else if (payload.id != null) {
        await measurementService.update(payload.id, payload);
      } else {
        await measurementService.create(payload);
      }
      if (isMountedRef.current) {
        setSaveState(SAVE_STATE.SAVED);
      }
      return true;
    } catch (error) {
      console.error(error);
      if (isMountedRef.current) {
        setSaveState(SAVE_STATE.ERROR);
      }
      return false;
    }
  }, [persist]);

  const scheduleSave = useCallback(
    (payload) => {
      if (!payload) {
        return;
      }
      pendingPayloadRef.current = payload;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (isMountedRef.current) {
        setSaveState(SAVE_STATE.SAVING);
      }
      timerRef.current = setTimeout(flush, debounceMs);
    },
    [debounceMs, flush]
  );

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingPayloadRef.current = null;
    if (isMountedRef.current) {
      setSaveState(SAVE_STATE.IDLE);
    }
  }, []);

  const saveStateLabel =
    saveState === SAVE_STATE.SAVING
      ? "Salvando…"
      : saveState === SAVE_STATE.SAVED
        ? "Alterações salvas"
        : saveState === SAVE_STATE.ERROR
          ? "Erro ao salvar"
          : "";

  return {
    saveState,
    saveStateLabel,
    scheduleSave,
    flush,
    cancelPending,
  };
}

export { SAVE_STATE };
